import geocode from "../../lib/geocode";
import Discover from "../../lib/Discover";
import calculateDistance from "../../utils/calculateDistance";
import calculateTime from "../../utils/calculateTime";
import calculateLegDetails from "../../utils/calculateLegDetails";
import { distributedCategories } from "../planner/categories";

const valueOf = (value) => typeof value === "function" ? value() : value;
const coordinate = (location) => ({ lat: valueOf(location.lat), lng: valueOf(location.lng) });

const metresBetween = (a, b) => {
  const radians = (degrees) => degrees * Math.PI / 180;
  const dLat = radians(b.lat - a.lat);
  const dLng = radians(b.lng - a.lng);
  const start = radians(a.lat);
  const end = radians(b.lat);
  const value = Math.sin(dLat / 2) ** 2 + Math.cos(start) * Math.cos(end) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
};

const plotPoints = (start, end, count) => Array.from({ length: count }, (_, index) => {
  const amount = count === 1 ? 0.5 : index / (count - 1);
  return { lat: start.lat + (end.lat - start.lat) * amount, lng: start.lng + (end.lng - start.lng) * amount };
});

const score = (place) => (place.rating || 0) * 2.2 + Math.log10((place.user_ratings_total || 0) + 1) * 1.4;
const candidates = (places, category) => places
  .filter((place) => place.place_id && place.geometry?.location && place.business_status !== "CLOSED_PERMANENTLY" && (place.rating || 0) >= 3.8)
  .sort((a, b) => score(b) - score(a))
  .map((place) => ({ ...place, category, stopType: category }));

const googleMode = (mode) => ({ walking: "WALKING", bicycling: "BICYCLING", transit: "TRANSIT", driving: "DRIVING" }[mode] || "WALKING");

const requestLeg = async (directionsService, origin, destination, mode, departureTime) => {
  const request = {
    origin,
    destination,
    travelMode: google.maps.TravelMode[googleMode(mode)], // eslint-disable-line no-undef
  };
  if (mode === "transit" && departureTime) request.transitOptions = { departureTime: new Date(departureTime * 1000) };
  try {
    const response = await directionsService.route(request);
    return { response, mode };
  } catch (error) {
    if (mode === "transit") return requestLeg(directionsService, origin, destination, "driving", departureTime);
    throw error;
  }
};

const mergeDirections = (responses) => {
  const firstRoute = responses[0]?.response?.routes?.[0];
  if (!firstRoute) return null;
  const legs = responses.flatMap(({ response }) => response.routes?.[0]?.legs || []);
  const overviewPath = responses.flatMap(({ response }) => response.routes?.[0]?.overview_path || []);
  return {
    ...responses[0].response,
    routes: [{ ...firstRoute, legs, overview_path: overviewPath }],
  };
};

export async function calculateRoute(
  startRef, finishRef, quantities, travelMode, directionsService,
  setDirectionsResponse, setDistance, setTime, setCombinedStops,
  setJourneyWarning, setRouteError, setSearchCoverage, setRouteLegs,
  plannerMode = "journey", localRadius = 1500, departureTime,
) {
  const startInput = startRef.current?.value?.trim();
  const finishInput = finishRef.current?.value?.trim();
  const categories = distributedCategories(quantities);
  if (!categories.length) { setRouteError("missing-stops"); return false; }
  if (!startInput || (plannerMode === "journey" && !finishInput)) {
    setRouteError(plannerMode === "local" ? "missing-location" : "missing-inputs");
    return false;
  }

  setRouteError("");
  setJourneyWarning("");
  const [startResult, finishResult] = await Promise.all([
    geocode(startInput),
    plannerMode === "local" ? Promise.resolve(null) : geocode(finishInput),
  ]);
  const start = { lat: startResult[0], lng: startResult[1] };
  const end = plannerMode === "local" ? start : { lat: finishResult[0], lng: finishResult[1] };
  if (![start.lat, start.lng, end.lat, end.lng].every(Number.isFinite)) { setRouteError("geocode-failed"); return false; }

  const maximumRadius = travelMode === "walking" ? 5000 : 15000;
  const routeDistance = metresBetween(start, end);
  const radius = plannerMode === "local"
    ? Math.min(maximumRadius, Math.max(500, Number(localRadius)))
    : Math.min(travelMode === "walking" ? 3000 : 7500, Math.max(travelMode === "walking" ? 400 : 750, routeDistance / Math.max(categories.length - 1, 1) * (travelMode === "walking" ? 0.6 : 0.85)));
  const points = plannerMode === "local" ? categories.map(() => start) : plotPoints(start, end, categories.length);
  setSearchCoverage({
    path: [start, end],
    points: plannerMode === "local" ? [{ ...start, stopType: "local", radius }] : points.map((point, index) => ({ ...point, stopType: categories[index], radius })),
  });

  try {
    const groups = await Promise.all(categories.map((category, index) => Discover(points[index].lat, points[index].lng, category, radius)));
    const used = new Set();
    const stops = [];
    groups.forEach((group, index) => {
      const place = candidates(group, categories[index]).find((candidate) => !used.has(candidate.place_id));
      if (place) {
        used.add(place.place_id);
        stops.push(place);
      }
    });
    if (stops.length !== categories.length) { setRouteError("places-failed"); return false; }
    if (plannerMode === "local") {
      stops.sort((a, b) => Math.atan2(a.geometry.location.lng - start.lng, a.geometry.location.lat - start.lat) - Math.atan2(b.geometry.location.lng - start.lng, b.geometry.location.lat - start.lat));
    }
    const pointsToRoute = [start, ...stops.map((stop) => coordinate(stop.geometry.location)), end];
    const legRequests = pointsToRoute.slice(1).map((destination, index) => {
      const requestedMode = travelMode === "smart" ? (metresBetween(pointsToRoute[index], destination) <= 1600 ? "walking" : "transit") : travelMode;
      return requestLeg(directionsService, pointsToRoute[index], destination, requestedMode, departureTime);
    });
    const responses = await Promise.all(legRequests);
    const merged = mergeDirections(responses);
    if (!merged) throw new Error("No route");
    const legs = calculateLegDetails(merged).map((leg, index) => ({ ...leg, mode: responses[index].mode }));
    setDirectionsResponse(merged);
    setCombinedStops(stops);
    setDistance(calculateDistance(merged));
    setTime(calculateTime(merged));
    setRouteLegs(legs);
    return true;
  } catch (error) {
    console.error(error);
    setRouteError("non-viable");
    return false;
  }
}
